/**
 * Incoming-call ringtone for desktop + mobile (esp. iPhone Chrome / Safari).
 *
 * Multi-tab: when one tab Accepts/Declines, ALL tabs must stop ringing.
 * Uses BroadcastChannel + localStorage so every open tab stops audio.
 */
import ringtoneSrc from "../assets/music/ringtone.mp3";

let audio = null;
let unlocked = false;
let unlockListenersBound = false;
let crossTabBound = false;
let ringing = false;
let playBlocked = false;
let retryTimer = null;
let vibrateTimer = null;
let audioCtx = null;
let oscTimer = null;
let activeAppointmentId = null;
const blockedListeners = new Set();
const stopListeners = new Set();

const BC_NAME = "prontopsy_ringtone";
const STORAGE_STOP_KEY = "prontopsy_ringtone_stop";
const STORAGE_RING_KEY = "prontopsy_ringtone_active";

function notifyBlocked() {
  blockedListeners.forEach((fn) => {
    try {
      fn(playBlocked);
    } catch (_) {
      /* ignore */
    }
  });
}

function notifyStopped(payload) {
  stopListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (_) {
      /* ignore */
    }
  });
}

export function subscribeRingtoneBlocked(fn) {
  if (typeof fn !== "function") return () => {};
  blockedListeners.add(fn);
  try {
    fn(playBlocked);
  } catch (_) {
    /* ignore */
  }
  return () => blockedListeners.delete(fn);
}

/** Called when ANY tab stops the ring (accept/decline/end) */
export function subscribeRingtoneStopped(fn) {
  if (typeof fn !== "function") return () => {};
  stopListeners.add(fn);
  return () => stopListeners.delete(fn);
}

export function isRingtonePlayBlocked() {
  return playBlocked;
}

export function isRingtoneUnlocked() {
  return unlocked;
}

export function isRingtonePlaying() {
  return ringing;
}

function getAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctx();
    } catch (_) {
      return null;
    }
  }
  return audioCtx;
}

function ensureAudio() {
  if (typeof Audio === "undefined") return null;
  if (!audio) {
    audio = new Audio();
    audio.src = ringtoneSrc;
    audio.loop = true;
    audio.preload = "auto";
    audio.playsInline = true;
    audio.setAttribute("playsinline", "true");
    audio.setAttribute("webkit-playsinline", "true");
    try {
      audio.load();
    } catch (_) {
      /* ignore */
    }
  }
  return audio;
}

function broadcastStop(reason, appointmentId) {
  if (typeof window === "undefined") return;
  const payload = {
    type: "stop",
    reason: reason || "accepted",
    appointmentId: appointmentId || activeAppointmentId || null,
    ts: Date.now(),
  };
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel(BC_NAME);
      bc.postMessage(payload);
      bc.close();
    }
  } catch (_) {
    /* ignore */
  }
  try {
    // storage event fires in OTHER tabs only
    localStorage.setItem(STORAGE_STOP_KEY, JSON.stringify(payload));
    localStorage.removeItem(STORAGE_RING_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Wire once: stop this tab when another tab accepts/declines */
export function bindRingtoneCrossTab() {
  if (crossTabBound || typeof window === "undefined") return;
  crossTabBound = true;

  const onStopMessage = (payload) => {
    if (!payload || payload.type !== "stop") return;
    // Local stop without re-broadcast loop
    stopRingtoneLocal();
    notifyStopped(payload);
  };

  try {
    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel(BC_NAME);
      bc.onmessage = (ev) => onStopMessage(ev?.data);
    }
  } catch (_) {
    /* ignore */
  }

  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_STOP_KEY && e.newValue) {
      try {
        onStopMessage(JSON.parse(e.newValue));
      } catch (_) {
        onStopMessage({ type: "stop" });
      }
    }
  });
}

export async function unlockRingtoneFromGesture() {
  const a = ensureAudio();
  const ctx = getAudioContext();

  if (ctx && ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (_) {
      /* ignore */
    }
  }

  if (!a) return unlocked;

  try {
    const prevVol = a.volume;
    a.muted = false;
    a.volume = 0.01;
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      await p;
    }
    a.pause();
    a.currentTime = 0;
    a.volume = prevVol || 1;
    unlocked = true;
    playBlocked = false;
    notifyBlocked();
  } catch (_) {
    try {
      a.muted = true;
      await a.play();
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      a.volume = 1;
      unlocked = true;
      playBlocked = false;
      notifyBlocked();
    } catch (__) {
      /* still blocked */
    }
  }
  return unlocked;
}

export function bindRingtoneUnlock() {
  if (unlockListenersBound || typeof document === "undefined") return;
  unlockListenersBound = true;
  ensureAudio();
  bindRingtoneCrossTab();

  const onGesture = () => {
    unlockRingtoneFromGesture();
  };

  document.addEventListener("touchstart", onGesture, { capture: true, passive: true });
  document.addEventListener("touchend", onGesture, { capture: true, passive: true });
  document.addEventListener("click", onGesture, { capture: true, passive: true });
  document.addEventListener("keydown", onGesture, { capture: true, passive: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ringing) {
      attemptPlay();
    }
  });
}

function stopFallbackBeep() {
  if (oscTimer) {
    clearInterval(oscTimer);
    oscTimer = null;
  }
}

function startFallbackBeep() {
  stopFallbackBeep();
  const ctx = getAudioContext();
  if (!ctx) return;

  const beep = () => {
    if (!ringing) return;
    try {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        try {
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
          osc.stop(ctx.currentTime + 0.16);
        } catch (_) {
          try {
            osc.stop();
          } catch (__) {
            /* ignore */
          }
        }
      }, 180);
    } catch (_) {
      /* ignore */
    }
  };

  beep();
  oscTimer = setInterval(beep, 1400);
}

function startVibrateLoop() {
  stopVibrateLoop();
  if (!navigator.vibrate) return;
  const pulse = () => {
    if (!ringing) return;
    try {
      navigator.vibrate([400, 200, 400, 200, 400]);
    } catch (_) {
      /* ignore */
    }
  };
  pulse();
  vibrateTimer = setInterval(pulse, 2000);
}

function stopVibrateLoop() {
  if (vibrateTimer) {
    clearInterval(vibrateTimer);
    vibrateTimer = null;
  }
  try {
    if (navigator.vibrate) navigator.vibrate(0);
  } catch (_) {
    /* ignore */
  }
}

function attemptPlay() {
  if (!ringing) return;
  const a = ensureAudio();
  if (!a) return;

  try {
    a.loop = true;
    a.muted = false;
    a.volume = 1;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        playBlocked = false;
        notifyBlocked();
        stopFallbackBeep();
      }).catch(() => {
        playBlocked = true;
        notifyBlocked();
        startFallbackBeep();
      });
    }
  } catch (_) {
    playBlocked = true;
    notifyBlocked();
    startFallbackBeep();
  }
}

/** Stop audio in THIS tab only (no rebroadcast) */
function stopRingtoneLocal() {
  ringing = false;
  playBlocked = false;
  activeAppointmentId = null;
  notifyBlocked();
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
  stopFallbackBeep();
  stopVibrateLoop();
  try {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * Stop ringtone in THIS tab and tell all other tabs to stop too.
 * @param {string} [reason]
 * @param {string} [appointmentId]
 */
export function stopRingtone(reason, appointmentId) {
  const appt = appointmentId || activeAppointmentId;
  stopRingtoneLocal();
  // Only broadcast + dismiss for real hang-up / accept / decline.
  // Effect cleanup ("modal-close") must NOT mark dismissed — that killed the next ring.
  const DISMISS = new Set([
    "accepted",
    "decline",
    "reject",
    "disconnect",
    "chat-accepted",
  ]);
  const shouldDismiss = DISMISS.has(String(reason || ""));
  if (shouldDismiss) {
    broadcastStop(reason || "stop", appt);
    try {
      if (appt) {
        sessionStorage.setItem(`ringDismissed:${appt}`, "1");
      }
    } catch (_) {
      /* ignore */
    }
  }
}

/**
 * Start looping ringtone. Safe to call multiple times.
 * @param {string} [appointmentId] — used so other tabs can dismiss this ring
 */
export function startRingtone(appointmentId) {
  bindRingtoneCrossTab();

  // Don't ring if this appointment was already accepted/declined in another tab
  if (appointmentId) {
    try {
      if (sessionStorage.getItem(`ringDismissed:${appointmentId}`) === "1") {
        return;
      }
    } catch (_) {
      /* ignore */
    }
    activeAppointmentId = String(appointmentId);
    try {
      localStorage.setItem(
        STORAGE_RING_KEY,
        JSON.stringify({ appointmentId: activeAppointmentId, ts: Date.now() }),
      );
    } catch (_) {
      /* ignore */
    }
  }

  ringing = true;
  ensureAudio();
  startVibrateLoop();
  attemptPlay();

  if (retryTimer) clearInterval(retryTimer);
  retryTimer = setInterval(() => {
    if (!ringing) {
      clearInterval(retryTimer);
      retryTimer = null;
      return;
    }
    attemptPlay();
  }, 700);
}

export async function startRingtoneFromUserGesture(appointmentId) {
  await unlockRingtoneFromGesture();
  startRingtone(appointmentId);
  attemptPlay();
  return !playBlocked;
}

export function getRingtoneAudio() {
  return ensureAudio();
}
